/**
 * 首页 —— **家庭净资产 Hero（J 域 J1）** + 最近记录 + 快速入口
 *
 * ## 🔴 本批换了什么（㉑ · J 域净资产端点落地）
 *
 * 上一批（⑳）本页的 Hero 显示的是「日常账户余额合计」，并**在界面上明写"这不是净资产"**——
 * 那是对的（宁可显示得少，也不制造第二份口径）。现在 `/api/networth` 落地，
 * Hero **换成一个字段**：`netWorth`，值由服务端 `composeNetWorth`（资产 10 类 / 负债 6 类）下发。
 *
 * ## 🔴 端侧三条纪律（本页逐条兑现）
 *
 * | # | 纪律 | 本页怎么做的 |
 * |---|---|---|
 * | ① | **不重算派生值** | 净资产 / 总资产 / 总负债 / 最近记录的金额**全部**用服务端下发的字段（`netWorth`、`parent.net`）；**没有任何 `reduce` 求和** |
 * | ② | **金额只经共享内核** | 一律走 `<Amount cents=… currency=… />`（内部是共享内核 `formatMoneyText()` + 千分位），**不写 `cents / 100`** |
 * | ③ | **不自己编文案** | 「未就绪」清单直接用服务端 `notReady[].label` / `.missing`，端侧不重命名 |
 *
 * ## 🔴 为什么"未就绪"必须显示出来，而不是画个 0
 *
 * 资产方第 8/9/10 类（手动资产 / 重大资产 / 投资持仓）与负债方第 4 类（分期本金）
 * **接口还没写** ⇒ 服务端诚实地把两侧合计标成 `partial: true`（数字是**下界**）。
 * 若本页把它们画成 `¥0`，用户会以为"我家没有这些" —— 那是**用沉默制造错误信息**。
 *
 * ## ⚠️ 配色依据（铁律 8 + 原型）
 * 净资产 / 总资产 / 总负债**一律中性色**（`colored={false}`）——
 * 原型 `01-今天_原型.html` 的 Hero 三数都没有语义色类（`.hero-num` 无色类；
 * `总资产` 上的 `class="down"` 在原型 CSS 里**没有任何定义**，是死类），
 * 且 J 域 PRD §4.8 明文「资产/负债总计用中性强调色，**不做红绿对立**」。
 * ⇒ 红色只用于"已超 / 逾期"，不用于"负债"（负债是常态不是错误）。
 */

import { Alert, Box, Button, Card, CardActionArea, CardContent, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { lazy, Suspense, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CurveRange, type CurveRange as CurveRangeType } from '@jiaying/shared';
import { Loading } from '../ui/Loading.js';

/**
 * 🔴 **图表库按需加载**（2026-10-02 实测触发的一次架构级处置）。
 *
 * ## 事实
 * 引入 Recharts 后客户端产物 **703 KB → 1076 KB**（gzip 222 → **325 KB**，**+46%**）。
 * 而架构 §15.1-8 对 MUI 定过同类判据：「映射成本逼近触发线 ⇒ 回看替代方案」——
 * 曲线在首页**首屏下方**（要在净资产卡与最近记录之后才滚到），**没有理由**让它拖慢首屏。
 *
 * ## 处置
 * `React.lazy` + `Suspense` ⇒ Recharts 及其传递依赖（实测 42 包，含 `react-redux` / `immer`）
 * 被 Vite **切成独立 chunk**，只在滚动到图表时才下载。⚠️ 这不是"以后再说"的优化 ——
 * 它是**让 Chart 这一选型继续成立的前提**（否则就是拿首屏换图表）。
 *
 * ⚠️ 若将来 K 域报表页也要图表，**同一个 lazy 边界可复用**（别再各写一处 `import`）。
 */
const NetWorthChart = lazy(() =>
  import('../ui/NetWorthChart.js').then((m) => ({ default: m.NetWorthChart })),
);
import { getNetWorth, listAccounts, listTransactions } from '../api/endpoints.js';
import {
  chargeBackHint,
  excludedText,
  isFullyRefunded,
  refundHint,
  rowAmount,
  rowSubtitle,
  rowTitle,
} from '../features/journal-labels.js';
import { canRefund, refundCategoryId } from '../features/refund-entry.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { Amount } from '../ui/Amount.js';
import { Grid } from '../ui/Grid.js';
import { SpendableCard } from '../ui/SpendableCard.js';
// 🆕 批 130：未来 30 天现金流（可用现金 vs 必付 —— 与 SpendableCard **两套口径**）
import { CashCoverageCard } from '../ui/CashCoverageCard.js';

export function HomePage() {
  const navigate = useNavigate();
  const net = useQuery({ queryKey: ['networth'], queryFn: getNetWorth });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  // ⚠️ `listTransactions` 现在收「拼好的查询串」（㊼ 批起）⇒ 这里显式传空串
  //（首页「最近记录」只用服务端默认态的第一页，不做筛选）
  const txns = useQuery({ queryKey: ['transactions'], queryFn: () => listTransactions('') });
  // 🔴 曲线档位是**端侧状态**（三值枚举由契约定死；服务端只按它取数窗口）
  const [range, setRange] = useState<CurveRangeType>(CurveRange.M12);
  /**
   * 🔴 内容区引用 —— 交给 `<AnchorBar>` 做吸顶页内导航（`AC-O2.36`）。
   *
   * ⚠️ 必须指向**内容区的直接容器**（本页 = 最外层 `<Stack>`）：
   * 组件只遍历它的**直接子元素**（与原型 `content.children` 一致）。
   * 若改指向某个卡片内部，锚点就会数走样。
   */
  const contentRef = useRef<HTMLDivElement | null>(null);

  // 🔴 三条守卫缺一不可 —— `isPending` 只排除"还在加载"，**不排除"已失败"**
  //    （失败态的 `data` 同样是 `undefined`）。少写 `isError` 那条，
  //    `accounts.data.items` 就会报 `possibly 'undefined'`。
  if (net.isPending) return <Loading />;
  if (accounts.isPending) return <Loading />;
  if (net.isError) {
    return <Alert severity="error">净资产读取失败：{String(net.error)}</Alert>;
  }
  if (accounts.isError) {
    return <Alert severity="error">账户读取失败：{String(accounts.error)}</Alert>;
  }

  const all = accounts.data.items;
  const nw = net.data;

  return (
    <Stack spacing={2} ref={contentRef}>
      {/*
        🆕 🔴 **吸顶页内导航**（`AC-O2.36`）—— 必须是内容区的**第一个子节点**：
        组件把 `position: sticky; top: 58px` 的锚点条画在这里，并把**其余兄弟块**当成锚点候选。
        ⚠️ 本组件**自带 `data-no-anchor`** ⇒ 它不会把自己也做成一个锚点。
        ⚠️ 短页时它在 effect 里判定不产出（返回 `null`）—— 本页不会出现"空条"。
      */}
      <AnchorBar contentRef={contentRef} />

      {/* ── Hero：家庭净资产（🔴 唯一口径 = 服务端 `composeNetWorth`） ── */}
      <Card data-anchor="净资产概览">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                家庭净资产
              </Typography>
              <Amount
                cents={nw.netWorth}
                currency={nw.baseCurrency}
                colored={false}
                variant="h4"
                sx={{ fontWeight: 700, my: 0.5 }}
              />
            </Box>
            <Stack spacing={1.5} sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  总资产
                </Typography>
                <Amount cents={nw.totalAssets} currency={nw.baseCurrency} colored={false} variant="subtitle1" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  总负债
                </Typography>
                <Amount cents={nw.totalLiabilities} currency={nw.baseCurrency} colored={false} variant="subtitle1" />
              </Box>
            </Stack>
          </Stack>

          {/*
            🔴 `partial` 由**服务端**判定（它知道哪些来源还没有接口）——
            本页只负责把事实说出来，不自己猜"还差哪些"。
          */}
          {nw.partial && (
            <Alert severity="info" icon={false} sx={{ mt: 2 }}>
              <Typography variant="caption" component="div">
                <strong>{nw.notReady.length} 类暂无数据来源</strong>（
                {nw.notReady.map((x) => x.label).join(' / ')}）⇒ 以上数字是<strong>下界</strong>，
                不是最终家底。
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                {nw.notReady.map((x) => `${x.label}：${x.missing}`).join('；')}
              </Typography>
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              账户共 {all.length} 个（日常 {all.filter((a) => !a.hidden).length} · 社保{' '}
              {all.filter((a) => a.hidden).length}）
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/*
        ── 🆕 未来 30 天现金流（批 `130` · 负责人 2026-10-31 第 7 条）──

        🔴 为什么放在**这里**（Hero 之后、曲线之前）：用户的原话是
        「把将来一个月需要动的现金流**体现在首页**」—— 它要和"我手上有多少钱"挨着看，
        才有"够不够付"的意义。放到页面底部就变成一条无人看的统计。

        🔴 它**不是**「本月剩余可花」的第二份：那张的分母是**预算额度**，
        这张的分母是**账户余额**。两张卡并排时靠各自的口径行区分（`DR-010` 决策三）。
      */}
      <CashCoverageCard />

      {/*
        ── 净资产曲线（J 域 J2）──
        🔴 「诚实性」的三条约束全部由 `NetWorthChart` 落实（只绘 live / 空态不画线 / 不补点）——
        本页**不参与**那些判断，只负责传档位（否则就是第二处口径）。
        🔴 `Suspense` fallback 用 `<Loading/>`（不是空白 —— 空白会让人以为"这里就是没有内容"）。

        🆕 网格化（㊱）：曲线是**宽图表**，与右侧「最近记录」并排会把它压到不足 700px ——
        坐标轴与折线会挤在一起。⇒ 按设计的 `.g2` 等宽双列把它**整行铺满**
        （设计里曲线也在 Hero 卡的独立整行内，见 `01-今天_原型.html:301`）。
      */}
      <Suspense fallback={<Loading py={3} />}>
        <NetWorthChart range={range} onRangeChange={setRange} />
      </Suspense>

      {/*
        ── 下方两栏：最近记录（主 · 1.62fr） + 账户速览（侧 · 1fr）──
        🔴 列宽比 **1.62 : 1** 是**照抄设计**（`01-今天_原型.html:454` 的主/侧栏网格），
        不是随手定的整数比 —— 主栏要放「逐条记录」（信息量大），侧栏只放聚合数。
        ⇒ 走 `Grid` 的 `templateColumns` 逃生舱（等宽 `.g2` 在这里会让侧栏显得空）。

        🆕🔴 `data-anchor`（`AC-O2.36`）：这一整块是**排版网格**，它内部的两张卡片
        各有标题 ⇒ 按锚点规则会**下探**到卡片级（每个子块都有标题那条判据）。
        这里显式给父网格一个锚点文案，是为了让**下探失败时**（如某张卡片被条件渲染掉）
        父块仍有可读文案 —— 不写的话它只会**静默消失**，不报错。
      */}
      <Grid templateColumns="minmax(0, 1.62fr) minmax(0, 1fr)" sx={{ alignItems: 'start' }}>
        {/* ── 最近记录 ── */}
        <Card data-anchor="最近记录">
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              最近记录
            </Typography>
          </CardContent>
          <List dense>
            <Divider />
            {(txns.data?.items ?? []).slice(0, 8).map((t) => (
              <ListItem key={t.id} divider>
                <ListItemText
                  /*
                    🆕 批 `115`（`DR-066` D2 · `AC-B5.4`）：**删掉手写的 primary / secondary**，
                    改调流水页那一对函数 —— 「同一笔在两页的首行文案必须完全一致」是验收条款，
                    靠"两边都记得改"是保不住的，只能靠同一个实现。
                    ⚠️ 副行额外补「不计收支」标注（`AC-C1.7`）：流水页那边它是 Chip，
                    这里退化成一句小字，但**判据仍取服务端派生的 `parent.hasExcludedLegs`**。
                  */
                  primary={rowTitle(t)}
                  secondary={`${rowSubtitle(t)}${
                    // 🆕 批 `120`（`DR-069` D3 / D5）：被退过的写「已退 · 原额」，退款行写「冲减：…」
                    refundHint(t) === null ? '' : ` · ${refundHint(t) ?? ''}`
                  }${
                    chargeBackHint(t) === null ? '' : ` · ${chargeBackHint(t) ?? ''}`
                  }${
                    excludedText(t) === null ? '' : ` · ${excludedText(t) ?? ''}`
                  }`}
                />
                {/*
                  🔴 **退款入口**（`DR-035` v1.1 · C 域 PRD §5.2.3）——
                  只对**支出**出现（收入 / 转账不能被冲减，服务端 `checkRefundParent` 会拦）。

                  🆕 批 `115`（`DR-066` §5 · `AC-B5.12`）：判据抽到 `features/refund-entry.ts`，
                  与流水页**同一个函数**（修前这里是内联条件，流水页想加就得抄第二份）。
                */}
                {canRefund(t) && (
                  <Button
                    size="small"
                    data-testid={`refund-${t.id}`}
                    onClick={() =>
                      void navigate('/new', {
                        state: {
                          refund: {
                            id: t.id,
                            accountId: t.parent.mainAccountId,
                            note: t.note,
                            date: t.date,
                            net: t.parent.net,
                            // 🔴 批 `119`：退款腿要挂**原支出分类**（`isRefundTxn` 的判据），
                            //    取值口径的唯一实现 = `refundCategoryId()`
                            categoryId: refundCategoryId(t),
                            // 🆕 批 `120`（`DR-069` D6）：已退多少 —— 退款页算「最多可退」用
                            refundedAmount: t.refunds?.amount ?? 0,
                          },
                        },
                      })
                    }
                    sx={{ mr: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    退款
                  </Button>
                )}
                {/*
                  🔴 批 `120`（`DR-069` D1）：金额**改走 `rowAmount()`** —— 与流水页、报表下钻同一个实现。
                  修前这里自己判转账 / 直接取 `parent.net`，于是"被退款后的净额"只改了流水页、
                  首页仍是全额 ⇒ 同一笔在两页显示不同的数（铁律 24 说的就是这种分叉）。
                */}
                {isFullyRefunded(t) ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    data-testid={`home-fully-refunded-${t.id}`}
                  >
                    已全额退款
                  </Typography>
                ) : (
                  <Amount
                    cents={rowAmount(t).cents}
                    currency={t.parent.currency}
                    colored={rowAmount(t).colored}
                    signed={rowAmount(t).signed}
                    variant="body2"
                  />
                )}
              </ListItem>
            ))}
            {txns.isSuccess && txns.data.items.length === 0 && (
              <ListItem>
                <ListItemText secondary="还没有记录 —— 点右上角「记一笔」开始" />
              </ListItem>
            )}
            {txns.isError && (
              <ListItem>
                <ListItemText secondary={`读取失败：${String(txns.error)}`} />
              </ListItem>
            )}
          </List>
        </Card>

        {/* ── 侧栏：可花 + 账户速览（点进去看明细） ── */}
        <Stack spacing={2}>
          {/*
            🆕 🔴 **本月剩余可花（J 域 J3）** —— `GET /api/spendable`（规格 §12 / `DR-010`）。
            🔴 它是**四卡区**的第一张卡（原型 `01-今天_原型.html:372`）—— 本批只上这一张：
            另三张（餐饮预算 / 本月结余 / 支出构成）分属 G 域与 K 域，**没做完就不画**
            （「不画灰项」，㊱ 批的纪律）。放在侧栏是为了不动现有主/侧栏比例（`1.62 : 1`）。
            ⚠️ 内部是自己取数的整卡组件（与 `ContributionCard` 同范式），本页不参与它的口径。
          */}
          <SpendableCard />
          <Card>
            <CardActionArea onClick={() => navigate('/accounts')}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  账户
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  共 {all.length} 个（日常 {all.filter((a) => !a.hidden).length} · 社保{' '}
                  {all.filter((a) => a.hidden).length}）
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
          <Card>
            <CardActionArea onClick={() => navigate('/policies')}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  保单
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  保险是净资产的组成部分 —— 现金价值按状态计入
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Stack>
      </Grid>
    </Stack>
  );
}
